import React, { useState, useMemo } from 'react';
import { Menu } from 'antd';
import { Contaniner } from './NavLinks.style';
import { SideBarMenus, getValidMenus } from 'utils';
import { useAuth } from 'hooks';

// Helper to get UI version from localStorage
const getUIVersion = () => localStorage.getItem('uiVersion') || 'old';

// Helper to filter children without v2Only flag
const filterV2OnlyChildren = (children) =>
	children ? children.filter((child) => !child.v2Only) : [];

// Helper to transform oldUILink items in old UI
const transformOldUILinkForOldUI = (item) => {
	// Filter out v2Only children and also remove "Invoice Edit Approval" since it's a separate menu item in old UI
	const filteredChildren = filterV2OnlyChildren(item.children).filter(
		(child) => child.key !== 'approval-invoice-edit-approval'
	);

	// If there are children to show, keep them; otherwise make it a simple link
	if (filteredChildren.length > 0) {
		return {
			...item,
			children: filteredChildren,
			oldUILink: undefined, // Remove oldUILink so it behaves as expandable menu
		};
	}
	// No children to show, make it a simple clickable link
	const { children, oldUILink, ...rest } = item;
	return rest;
};

// Helper to transform oldUILink items in new UI
const transformOldUILinkForNewUI = (item) => {
	const labelText = item.label?.props?.children || 'Approvals';
	return {
		...item,
		label: labelText,
		linkto: undefined, // Remove link in new UI
	};
};

// Helper to transform menu item based on UI version
const transformMenuItem = (item, uiVersion) => {
	if (item.oldUILink) {
		return uiVersion === 'old'
			? transformOldUILinkForOldUI(item)
			: transformOldUILinkForNewUI(item);
	}

	// Filter out children with v2Only flag when in old UI
	if (item.children && uiVersion === 'old') {
		return {
			...item,
			children: filterV2OnlyChildren(item.children),
		};
	}

	return item;
};

export default function NavLinks() {
	const auth = useAuth();
	const uiVersion = getUIVersion();
	// trips vehicles clients vendor drivers users
	const [openKeys, setOpenKeys] = useState(['trips']);

	// Filter out v2Only items when UI version is not 'new' and handle oldUILink
	const filteredMenus = useMemo(() => {
		const menus =
			uiVersion === 'new'
				? SideBarMenus.filter((item) => !item.oldUIOnly) // Remove oldUIOnly items in new UI
				: SideBarMenus.filter((item) => !item.v2Only); // Remove v2Only items in old UI

		// Transform menu items based on UI version
		return menus.map((item) => transformMenuItem(item, uiVersion));
	}, [uiVersion]);

	const rootSubmenuKeys = [
		'trips',
		'vehicles',
		'clients',
		'vendor',
		'drivers',
		'users',
		'control_tower',
		'approval_entities', // Now expandable in both old and new UI
	];

	const onOpenChange = (keys) => {
		const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
		if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
			setOpenKeys(keys);
		} else {
			setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
		}
	};

	return (
		<Contaniner>
			<Menu
				openKeys={openKeys}
				onOpenChange={onOpenChange}
				items={getValidMenus(filteredMenus, auth, uiVersion)}
				mode="inline"
				inlineCollapsed={false}
			/>
		</Contaniner>
	);
}
